import { useTranslation } from 'react-i18next';
import { useCheckIn } from '@/hooks/useCheckIn';

interface CheckInWidgetProps {
  staffName: string;
}

export default function CheckInWidget({ staffName }: CheckInWidgetProps) {
  const { t } = useTranslation();
  const {
    record,
    loading,
    locationLoading,
    isCheckedIn,
    isCheckedOut,
    checkIn,
    checkOut,
  } = useCheckIn(staffName);

  const handleCheckIn = async () => {
    await checkIn();
  };

  const handleCheckOut = () => {
    checkOut();
  };

  // ── Not checked in yet ──
  if (!isCheckedIn) {
    return (
      <div className="bg-white rounded-2xl border-2 border-dashed border-accent-400/60 p-5 sm:p-6 mb-6 relative overflow-hidden">
        <div
          className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-6"
          style={{
            background: 'radial-gradient(circle, oklch(0.58 0.22 38), transparent 70%)',
            transform: 'translate(30%, -30%)',
          }}
        />
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-5">
          <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
            <i className="ri-fingerprint-line text-accent-600 text-2xl" />
          </div>
          <div className="flex-1 text-center sm:text-left min-w-0">
            <h3 className="font-heading font-700 text-foreground-900 text-lg mb-1">
              {t('dashboard.checkInTitle')}
            </h3>
            <p className="text-foreground-500 text-sm font-label">
              {t('dashboard.checkInDescription')}
            </p>
            {locationLoading && (
              <p className="text-xs text-accent-600 font-label mt-1.5 flex items-center justify-center sm:justify-start gap-1">
                <i className="ri-radar-line animate-pulse" />
                {t('dashboard.capturingLocation')}
              </p>
            )}
          </div>
          <button
            onClick={handleCheckIn}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 sm:py-2.5 bg-accent-500 text-background-50 text-sm font-label font-semibold rounded-xl hover:bg-accent-600 active:scale-[0.97] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto justify-center"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base" />
                {t('dashboard.checkingIn')}
              </>
            ) : (
              <>
                <i className="ri-login-box-line text-lg" />
                {t('dashboard.checkInNow')}
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ── Checked in, not yet out ──
  if (!isCheckedOut) {
    return (
      <div className="bg-white rounded-2xl border border-accent-300/50 p-5 sm:p-6 mb-6 relative overflow-hidden">
        <div
          className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-6"
          style={{
            background: 'radial-gradient(circle, oklch(0.58 0.22 38), transparent 70%)',
            transform: 'translate(30%, -30%)',
          }}
        />
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-5">
          <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded-full bg-semantic-success-bg relative">
            <i className="ri-check-line text-semantic-success text-2xl" />
            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-semantic-success border-2 border-white animate-pulse" />
          </div>
          <div className="flex-1 text-center sm:text-left min-w-0">
            <h3 className="font-heading font-700 text-foreground-900 text-lg mb-1">
              {t('dashboard.checkedInTitle')}
            </h3>
            <p className="text-foreground-500 text-sm font-label">
              {t('dashboard.checkedInAt')}{' '}
              <span className="font-semibold text-foreground-700">{record?.checkInTime}</span>
              {record?.locationName &&
                record.locationName !== 'Location not captured' &&
                record.locationName !== 'Location unavailable' && (
                  <>
                    {' '}
                    &middot;{' '}
                    <span className="inline-flex items-center gap-1 text-foreground-600">
                      <i className="ri-map-pin-line text-xs" />
                      {record.locationName}
                    </span>
                  </>
                )}
            </p>
            <p className="text-xs text-foreground-400 font-label mt-1">
              {t('dashboard.dontForget')}
            </p>
          </div>
          <button
            onClick={handleCheckOut}
            className="flex items-center gap-2 px-6 py-3 sm:py-2.5 bg-foreground-100 text-foreground-700 text-sm font-label font-semibold rounded-xl hover:bg-foreground-200 active:scale-[0.97] transition-all cursor-pointer whitespace-nowrap border border-foreground-200 w-full sm:w-auto justify-center"
          >
            <i className="ri-logout-box-r-line text-lg" />
            {t('dashboard.checkOut')}
          </button>
        </div>
      </div>
    );
  }

  // ── Day complete (checked out) ──
  return (
    <div className="bg-white rounded-2xl border border-background-200 p-5 sm:p-6 mb-6">
      <div className="flex flex-col sm:flex-row items-center gap-5">
        <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded-full bg-background-100">
          <i className="ri-calendar-check-line text-foreground-400 text-2xl" />
        </div>
        <div className="flex-1 text-center sm:text-left min-w-0">
          <h3 className="font-heading font-700 text-foreground-900 text-lg mb-1">
            {t('dashboard.dayComplete')}
          </h3>
          <p className="text-foreground-500 text-sm font-label">
            {t('dashboard.checkedInAt')}{' '}
            <span className="font-semibold text-foreground-700">{record?.checkInTime}</span> &middot;{' '}
            {t('dashboard.checkedOutAt')}{' '}
            <span className="font-semibold text-foreground-700">{record?.checkOutTime}</span>
            {record?.locationName &&
              record.locationName !== 'Location not captured' &&
              record.locationName !== 'Location unavailable' && (
                <>
                  {' '}
                  &middot;{' '}
                  <span className="inline-flex items-center gap-1 text-foreground-600">
                    <i className="ri-map-pin-line text-xs" />
                    {record.locationName}
                  </span>
                </>
              )}
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-foreground-50 rounded-lg border border-foreground-100">
          <i className="ri-time-line text-foreground-400 text-sm" />
          <span className="text-xs text-foreground-500 font-label">
            {t('dashboard.seeYouTomorrow')}
          </span>
        </div>
      </div>
    </div>
  );
}